import React, { useState, useRef, useEffect } from 'react';
import type { NavigationRailTypesProps, NavigationRailItem } from './NavigationRailTypes.types';
import { ChevronRightIcon, ChevronLeftIcon, ClockIcon, MagnifyingGlassIcon } from './icons';
import { Button } from '../Button/Button';
import { Divider } from '../Divider/Divider';
import { DropdownItemCollapsible } from '../DropdownItemCollapsible/DropdownItemCollapsible';
import { NavigationRailItem as NavigationRailItemComponent } from '../NavigationRailItem/NavigationRailItem';
import { Input } from '../Input/Input';
import { NavigationRailPanel } from '../NavigationRailPanel/NavigationRailPanel';

/**
 * Componente NavigationRailTypes del sistema de diseño Siesa
 *
 * Barra de navegación lateral que permite dos modos visuales:
 * - **Collapsed (Colapsado)**: 80px width, solo iconos con botón de búsqueda
 * - **Expanded (Expandido)**: 215px width, con labels y búsqueda integrada
 *
 * **Características:**
 * - Dos modos: collapsed e expanded (toggle con botón "Colapsar")
 * - Soporta items con badges de notificación
 * - Items colapsables para agrupar navegación relacionada
 * - Botón "Recientes" en footer
 * - Búsqueda integrada (solo en modo expanded)
 * - Dark mode completo
 * - Focus rings adaptativos
 *
 * **Especificaciones de Figma:**
 * - Collapsed: 80px width, items con height 44px + gap 12px
 * - Expanded: 215px width, items reutilizan DropdownItemCollapsible
 * - Icono search: 16x16px, azul (#0e79fd)
 * - Borde derecho: 1px solid #e4e4e7 (collapsed)
 * - Borde izquierdo: 1px solid #e4e4e7 (expanded)
 * - Height total: 900px (scroll si necesario)
 *
 * **Estructura:**
 * 1. Header (Search en expanded) - altura variable
 * 2. Menu & Destinations - flex 1 con items scrollables
 * 3. Footer - Actions con divider y botones
 *
 * **Estados de Items:**
 * - `default`: Text color content-primary
 * - `hover`: Fondo hover-overlay, icon color content-custom-primary
 * - `active`: Fondo custom-primary, text color custom-primary
 * - `disabled`: Opacity 50%, sin interacción
 *
 * **Colores (Light Mode):**
 * - Background: background-primary (#ffffff)
 * - Borders: border-primary (#e4e4e7)
 * - Text: content-primary (#18181b)
 * - Icons: content-secondary (#a1a1aa)
 * - Icon hover: content-custom-primary (#0e79fd)
 * - Divider: border-primary (#e4e4e7)
 * - Search input: bg-primary, border-primary, placeholder: content-tertiary
 *
 * **Colores (Dark Mode):**
 * - Background: background-primary/dark (#09090b)
 * - Borders: border-primary/dark (#3f3f46)
 * - Text: content-primary/dark (#fafafa)
 * - Icons: content-secondary/dark (#a1a1aa)
 * - Icon hover: content-custom-primary/dark (#bfe2fe)
 * - Divider: border-primary/dark (#3f3f46)
 *
 * **Tipografía:**
 * - Items labels: Paragraph/Small (14px, Regular, line-height 20px)
 * - Heading: Paragraph/Tiny (12px, Regular, line-height 16px) - si aplica
 * - Button labels: Label Small (14px, Bold)
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico
 * @see docs/spacing.md - Sistema de espaciado (gap-3, px-4, py-2, etc)
 * @see https://www.figma.com/design/5XNqf2YTxvwemxwo1LMQ6j/Siesa-UI-Kit?node-id=6135-36219
 *
 * @example
 * ```tsx
 * const navItems: NavigationRailItem[] = [
 *   {
 *     id: 'home',
 *     label: 'Home',
 *     icon: <HomeIcon />,
 *     active: true,
 *     onClick: () => navigate('/home'),
 *   },
 *   {
 *     id: 'users',
 *     label: 'Users',
 *     icon: <UsersIcon />,
 *     badge: true,
 *     onClick: () => navigate('/users'),
 *   },
 * ];
 *
 * const [isCollapsed, setIsCollapsed] = useState(true);
 *
 * <NavigationRailTypes
 *   collapsed={isCollapsed}
 *   items={navItems}
 *   showRecentButton={true}
 *   onCollapsedChange={setIsCollapsed}
 *   onItemClick={(index, item) => console.log('Selected:', item.id)}
 * />
 * ```
 *
 * @see NavigationRailTypes.types.ts - Props e interfaces
 */
export const NavigationRailTypes = React.forwardRef<
  HTMLDivElement,
  NavigationRailTypesProps
>(
  (
    {
      collapsed = true,
      items = [],
      showRecentButton = true,
      showSearchButton = true,
      onCollapsedChange,
      onItemClick,
      onItemHover,
      className,
      labels = {},
      onSearch,
    },
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const searchButtonRef = useRef<HTMLDivElement>(null);

    // Cerrar panel al hacer click fuera
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          isSearchPanelOpen &&
          panelRef.current &&
          !panelRef.current.contains(event.target as Node) &&
          searchButtonRef.current &&
          !searchButtonRef.current.contains(event.target as Node)
        ) {
          setIsSearchPanelOpen(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isSearchPanelOpen]);

    // Labels con valores por defecto
    const finalLabels = {
      searchButton: labels.searchButton ?? 'Search',
      searchPlaceholder: labels.searchPlaceholder ?? 'Buscar módulo',
      recentButton: labels.recentButton ?? 'Recientes',
      collapseButton: labels.collapseButton ?? 'Colapsar',
      expandButton: labels.expandButton ?? 'Expandir',
    };

    const handleCollapsedToggle = () => {
      // Si expandimos el rail, cerramos el panel de búsqueda flotante
      if (collapsed) {
        setIsSearchPanelOpen(false);
      }
      onCollapsedChange?.(!collapsed);
    };

    const handleSearchButtonClick = () => {
      setIsSearchPanelOpen(!isSearchPanelOpen);
    };

    const handleItemClick = (index: number, item: NavigationRailItem) => {
      item.onClick?.(new Event('click') as any);
      onItemClick?.(index, item);
      // Cerrar panel si se selecciona un item
      if (isSearchPanelOpen) {
        setIsSearchPanelOpen(false);
      }
    };

    const handleItemHover = (index: number, item: NavigationRailItem) => {
      onItemHover?.(index, item);
    };

    const handleSearchChange = (value: string) => {
      setSearchQuery(value);
      onSearch?.(value);
    };

    const handleInputSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleSearchChange(e.target.value);
    };

    // Transformar items para el panel
    const panelItems = items.map((item, index) => ({
      label: item.label,
      icon: item.icon,
      disabled: item.disabled,
      // Adaptar children si existen (usando lógica simple por ahora)
      isCollapsible: (item as any).children && (item as any).children.length > 0,
      children: (item as any).children,
      defaultOpen: (item as any).defaultOpen,
      onClick: () => handleItemClick(index, item),
    }));

    return (
      <div
        ref={ref}
        className={`
          ${collapsed ? 'w-[80px]' : 'w-[215px]'}
          h-full
          bg-background-primary dark:bg-background-primary
          ${collapsed ? 'border-r border-border-primary dark:border-border-primary' : 'border-l border-r border-border-primary dark:border-border-primary'}
          border-solid
          flex flex-col
          items-center
          relative
          transition-all duration-300
          ${className || ''}
        `}
      >
        {/* Panel de búsqueda flotante (solo visible en modo collapsed + open) */}
        {collapsed && isSearchPanelOpen && (
          <div
            ref={panelRef}
            className="absolute left-[80px] top-0 h-full z-50 shadow-xl"
          >
            <NavigationRailPanel
              height="100%"
              width={248}
              showHeading={true}
              heading={{
                label: 'Buscar',
                showDescription: false,
                showAction: true,
                actionLabel: 'Cerrar',
                onActionClick: () => setIsSearchPanelOpen(false),
              }}
              showSearch={true}
              search={{
                placeholder: finalLabels.searchPlaceholder,
                value: searchQuery,
                onChange: handleSearchChange,
              }}
              items={panelItems}
              className="h-full border-l border-border-primary dark:border-border-primary"
            />
          </div>
        )}

        {/* Sección de búsqueda - solo en modo expandido */}
        {!collapsed && (
          <div className="w-full flex flex-col gap-3 px-3 py-3 relative shrink-0">
            <Input
              placeholder={finalLabels.searchPlaceholder}
              value={searchQuery}
              onChange={handleInputSearchChange}
              startIcon={<MagnifyingGlassIcon className="size-4 text-content-secondary dark:text-content-secondary" />}
            />
            <Divider className="w-full" />
          </div>
        )}

        {/* Main content - Menu & Destinations */}
        <div
          className={`
            flex-1 flex flex-col
            items-center min-h-px min-w-px
            relative shrink-0
            px-0
            ${collapsed
              ? 'gap-7 py-2 w-[80px]'
              : 'gap-0.5 px-3 pb-3 pt-0 w-full bg-background-primary dark:bg-background-primary border-l dark:border-border-primary border-border-primary border-solid'
            }
          `}
        >
          {/* Botón Search en collapsed */}
          {collapsed && showSearchButton && (
            <div ref={searchButtonRef}>
              <Button
                type="default" // Highlight visual si está abierto se maneja con className
                size="xl"
                iconOnly
                leftIcon={<MagnifyingGlassIcon className="overflow-clip relative shrink-0 size-4 text-primary-inverse-content dark:text-primary-inverse-content" />}
                ariaLabel={finalLabels.searchButton}
                onClick={handleSearchButtonClick}
                className={isSearchPanelOpen ? 'bg-primary-500 ring-2 ring-offset-2 ring-primary-400' : ''}
              />
            </div>
          )}

          {/* Items container */}
          <div
            className={`
              flex flex-col items-center min-h-px min-w-px
              relative shrink-0
              flex-1
              overflow-y-auto
              scrollbar-thin
              [&::-webkit-scrollbar]:w-1.5
              [&::-webkit-scrollbar-track]:bg-transparent
              [&::-webkit-scrollbar-thumb]:bg-gray-200
              [&::-webkit-scrollbar-thumb]:rounded-full
              dark:[&::-webkit-scrollbar-thumb]:bg-gray-700
              hover:[&::-webkit-scrollbar-thumb]:bg-gray-300
              dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-600
              ${collapsed
                ? 'gap-3 leading-none w-[80px]'
                : 'gap-0.5 px-3 pb-3 pt-0 w-full'
              }
            `}
          >
            {/* Espaciador superior para mantener estética sin afectar sticky headers */}
            {!collapsed && <div className="h-3 shrink-0" />}

            {collapsed ? (
              // Items reales - collapsed mode
              <>
                {items.map((item, idx) => (
                  <NavigationRailItemComponent
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    icon={item.icon}
                    selected={item.active}
                    disabled={item.disabled}
                    badge={item.badge}
                    badgeCount={item.badgeCount}
                    ariaLabel={item.ariaLabel}
                    showLabelText={false}
                    onClick={() => handleItemClick(idx, item)}
                    onMouseEnter={() => handleItemHover(idx, item)}
                    onMouseLeave={item.onMouseLeave}
                  />
                ))}
              </>
            ) : (
              // Items reales - expanded mode
              <>
                {items.map((item, idx) => {
                  const isCollapsible = (item as any).children && (item as any).children.length > 0;
                  return (
                    <div key={item.id} className="w-full">
                      <DropdownItemCollapsible
                        label={item.label}
                        icon={item.icon}
                        selected={item.active}
                        disabled={item.disabled}
                        badge={item.badge}
                        badgeCount={item.badgeCount}
                        badgeColor={item.badgeColor as any}
                        showDivider={false}
                        showHeading={false}
                        isCollapsible={isCollapsible}
                        defaultOpen={(item as any).defaultOpen}
                        children={isCollapsible ? (item as any).children : undefined}
                        onClick={() => handleItemClick(idx, item)}
                        stickyHeader={true}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer - Actions */}
          <div className={`w-full flex flex-col gap-3 items-center relative shrink-0 ${!collapsed ? 'px-3' : ''}`}>
            <Divider className="w-full" />

            <div
              className={`
                flex flex-col gap-3 relative shrink-0 w-full
                ${collapsed ? 'items-stretch px-3' : 'items-stretch'}
              `}
            >
              {/* Botón Recientes */}
              {showRecentButton && collapsed && (
                <Button
                  type="outline"
                  size="base"
                  fullWidth
                  leftIcon={<ClockIcon className="size-4 text-primary-600 dark:text-primary-600" />}
                  ariaLabel={finalLabels.recentButton}
                />
              )}

              {showRecentButton && !collapsed && (
                <Button
                  type="outline"
                  size="base"
                  leftIcon={<ClockIcon className="size-4 text-primary-600 dark:text-primary-600" />}
                  fullWidth
                >
                  {finalLabels.recentButton}
                </Button>
              )}

              {/* Botón Colapsar/Expandir */}
              {collapsed ? (
                <Button
                  type="plain"
                  size="base"
                  fullWidth
                  leftIcon={<ChevronRightIcon className="overflow-clip relative shrink-0 size-4 text-background-inverse-custom-primary dark:text-background-inverse-custom-primary" />}
                  onClick={handleCollapsedToggle}
                  ariaLabel={finalLabels.expandButton}
                />
              ) : (
                <Button
                  type="outline"
                  size="base"
                  leftIcon={<ChevronLeftIcon className="overflow-clip relative shrink-0 size-4 text-content-custom-primary dark:text-content-custom-primary" />}
                  onClick={handleCollapsedToggle}
                  fullWidth
                >
                  {finalLabels.collapseButton}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

NavigationRailTypes.displayName = 'NavigationRailTypes';

export default NavigationRailTypes;
